import { defineStore } from 'pinia'
import { NewSupportTicket, SupportInquiryType, SupportTicket, SupportTicketStatus } from '@webapp/gp/GroupAdminModel'
import { useGroupAdminApi, gpUrl } from './GroupAdminApi'
import { AsyncData } from '@webapp/util/AsyncData'
import { AsyncDataStore } from '@webapp/util/AsyncDataStore'
import { assignExisting } from '@webapp/util/TypeScriptUtil'
import { DateTimeUtil } from '@webapp/util/LuxonUtil'
import { DateTime } from 'luxon'
import { useTokenManager } from '@webapp/auth/TokenManager'

export const useSupportTicketStore = defineStore('supportTickets', () => {
    const asyncSupportTickets = new Map<string, AsyncData<SupportTicket>>()

    const gp = useGroupAdminApi()
    const tokenManager = useTokenManager()

    async function addAsync(newTicket: NewSupportTicket) {
        const store = useSupportTicketStore() // use to get/return reactive store objects
        const addCoreAsync = async () => {
            const p = await gp.postPlainAsync(`support/tickets`, newTicket)
            return plainToSupportTicket(p)
        }            
        const newId = await AsyncDataStore.addToStoreMapAsync(store.asyncSupportTickets, "SupportTicket", t => t.id, addCoreAsync)
        return newId
    }

    function processExpandedSupportTickets(plainItems: any[], requestTime: DateTime) {
        // no expansion supported (yet)
        return AsyncDataStore.createItemMap(plainItems, "SupportTicket", plainToSupportTicket, t => t.id!)
    }

    async function addFileAsync(ticketId: string, file: File) {
        const postUrl = `${gpUrl}support/tickets/${ticketId}/files`
        const token = await tokenManager.getAccessTokenAsync()
        const formData = new FormData()
        formData.append('file', file)

        const response = await fetch(postUrl, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: formData,
        })

        if (!response.ok)
            throw new Error(`Failed to upload ticket file: ${response.status} ${response.statusText}`)
    }

    function plainToSupportTicket(p: any) {
        const t = assignExisting(new SupportTicket(), p)
        t.inquiryType = p.inquiryType ?? SupportInquiryType.Question
        t.status = p.status ?? SupportTicketStatus.Open
        t.createdDate = DateTimeUtil.fromAPI(p.createdDate)
        t.modifiedDate = DateTimeUtil.fromAPI(p.modifiedDate)
        return t        
    }

    return {
        asyncSupportTickets,

        addAsync,
        addFileAsync,
    }
})
